<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import type { Address } from "@types"
import type { SourceTypeChoice } from "@core/components/source/form/append/machine/source-append.machine"
import type { SourceAppendDonePayload } from "@core/components/source/form/append/source-append.types"
import { useTaskStore } from "@core/stores/task.store"

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    /**
     * État initial (pdl, pce, source) pour afficher directement le parcours
     */
    fromState?: SourceTypeChoice
    /**
     * Limite les types proposés (ex. uniquement PDL et PCE sur la page compteurs)
     */
    allowedSourceTypes?: SourceTypeChoice[]
    /**
     * Identifiant du bâtiment rattachant la source
     */
    buildingId?: string
    /**
     * Adresse du bâtiment (pour formulaire capteur)
     */
    address?: Address | null
}

interface Emits {
    (e: "cancel"): void
    (e: "done", _id_source?: string, source?: SourceAppendDonePayload): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    fromState: undefined,
    allowedSourceTypes: undefined,
    buildingId: undefined,
    address: null,
})

const emit = defineEmits<Emits>()

const taskStore = useTaskStore()

const { t } = useI18n({
    messages: {
        fr: {
            task_onboarding_title: "Mise à jour du bâtiment",
            task_onboarding_success: "Le bâtiment a été mis à jour.",
            task_onboarding_error: "La mise à jour du bâtiment a échoué.",
        },
    },
})

const visible = defineModel<boolean>("visible", { default: false })

/** Évite d’émettre `cancel` quand la modale se ferme après done. */
const skipVisibilityCancel = ref(false)

/**
 * État courant remonté par SourceAppendForm (pour le titre du header)
 */
const currentState = ref("")

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Titre du header selon l'état du formulaire
 */
const getHeaderTitle = computed(() => {
    if (currentState.value === "PdlForm")
        return "Ajouter un compteur électrique (PDL)"
    if (currentState.value === "PceForm") return "Ajouter un compteur gaz (PCE)"
    if (currentState.value === "SensorForm") return "Ajouter un capteur"
    const pdlPceOnly =
        props.allowedSourceTypes?.length === 2 &&
        props.allowedSourceTypes.includes("pdl") &&
        props.allowedSourceTypes.includes("pce")
    if (pdlPceOnly) return "Ajouter un compteur"
    return "Ajouter une source"
})

// ================================
// TÂCHE ONBOARDING (réponse PUT sign / task_building)
// ================================

/**
 * Extrait task_id + event_name pour {@link useTaskStore.followEvent} depuis la réponse API.
 */
function getOnboardingTaskFollowParams(
    raw: unknown
): { task_id: string; event_name: string } | null {
    if (!raw || typeof raw !== "object") return null
    const o = raw as Record<string, unknown>
    const task_id = o.task_id
    const event_name = o.event_name
    if (
        typeof task_id === "string" &&
        typeof event_name === "string" &&
        task_id.length > 0 &&
        event_name.length > 0
    ) {
        return { task_id, event_name }
    }
    return null
}

/**
 * Abonne le canal Pusher `task` au suivi de la tâche d’onboarding lancée par l’API après signature.
 */
function followOnboardingTaskFromDonePayload(
    source?: SourceAppendDonePayload
): void {
    const follow = getOnboardingTaskFollowParams(source?.taskOnboarding)
    if (!follow) return
    taskStore.followEvent(follow.task_id, follow.event_name, {
        taskname: "building.onboarding.user",
        taskToastTitle: t("task_onboarding_title"),
        taskToastSuccessMessage: t("task_onboarding_success"),
        taskToastFailureMessage: t("task_onboarding_error"),
        notification: true,
        autoDelete: true,
    })
}

// ================================
// INTERACTIONS
// ================================

function onCancel(): void {
    visible.value = false
    emit("cancel")
}

function onDialogVisibleUpdate(v: boolean): void {
    if (v) return
    if (skipVisibilityCancel.value) {
        skipVisibilityCancel.value = false
        return
    }
    onCancel()
}

function onDone(_id_source?: string, source?: SourceAppendDonePayload): void {
    skipVisibilityCancel.value = true
    visible.value = false
    followOnboardingTaskFromDonePayload(source)
    emit("done", _id_source, source)
}
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        :closable="true"
        position="top"
        :header="getHeaderTitle"
        :style="{ width: '60vw' }"
        :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
        @update:visible="onDialogVisibleUpdate"
        :pt="{
            content:
                'max-h-[calc(100dvh-10rem)] overflow-y-auto overflow-x-hidden flex justify-center items-start w-full py-2',
        }"
    >
        <SourceAppendForm
            :key="`append-${visible}-${buildingId ?? ''}-${fromState ?? ''}-${(allowedSourceTypes ?? []).join(',')}`"
            :open="visible"
            :from-state="fromState"
            :allowed-source-types="allowedSourceTypes"
            :building-id="buildingId"
            :address="address"
            @cancel="onCancel"
            @done="onDone"
            @update:state="currentState = $event"
            class="w-full max-w-3xl shrink-0"
        />
    </BaseDialog>
</template>
